import React, { useEffect, useState, useCallback } from 'react';
import { SERVER_ERROR } from 'Helpers/Utils';

// import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import Api from '../../../Helpers/ApiHandler';
import { URL_DASHBOARD } from 'Helpers/Paths';
import { logoutUser, updateUser } from '../../../Redux/Auth/index';
import { API_LOGOUT } from 'Helpers/ApiPaths';
import { useSelector } from 'react-redux';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import { MenuWrapper } from './Topbar.style';
import { toast } from 'react-toastify';
import { URL_PROFILE } from 'Helpers/Paths';
import blankImage from '../../../Assets/Images/blankImage.png';
import { API_PROFILE } from 'Helpers/ApiPaths';

const API = new Api({});

/**
 * @component
 * @description Professional top navigation bar component with user profile management.
 * Provides user avatar, profile access, logout functionality, and profile data synchronization.
 *
 * Key Features:
 * - **User Profile Display**: Shows user avatar with fallback to default image
 * - **Profile Menu**: Dropdown menu with profile and logout options
 * - **Logout Confirmation**: Professional confirmation dialog for logout
 * - **Profile Synchronization**: Automatic profile data fetching and updates
 * - **Session Management**: Secure logout with token invalidation
 * - **Loading States**: Visual feedback during API operations
 * - **Error Handling**: Comprehensive error handling with user feedback
 * - **Responsive Design**: Adapts to different screen sizes
 *
 * Profile Management:
 * - **Avatar Display**: Circular profile image with golden border
 * - **Default Fallback**: Blank image fallback for missing profile pictures
 * - **Real-time Updates**: Automatic profile data synchronization
 * - **Session Storage**: Updates session storage with latest profile data
 * - **Redux Integration**: Syncs profile data with application state
 *
 * Menu Features:
 * - **Profile Access**: Direct navigation to profile management page
 * - **Logout Option**: Secure logout with confirmation dialog
 * - **Professional Styling**: Modern dropdown menu with hover effects
 * - **Icon Integration**: Material-UI icons for visual clarity
 * - **Keyboard Navigation**: Full accessibility support
 *
 * Security Features:
 * - **Token-based Logout**: Secure API logout with access token
 * - **Session Cleanup**: Proper cleanup of user session data
 * - **Confirmation Dialog**: Prevents accidental logout
 * - **Error Recovery**: Graceful error handling for failed operations
 *
 * @returns {JSX.Element} Top navigation bar with user profile functionality
 *
 * @example
 * // Basic usage in main layout
 * <AppBar position="sticky">
 *   <Toolbar>
 *     <Typography variant="h6" sx={{ flexGrow: 1 }}>
 *       Admin Panel
 *     </Typography>
 *     <Topbar />
 *   </Toolbar>
 * </AppBar>
 *
 * @example
 * // Usage in responsive layout
 * <Box sx={{ display: 'flex' }}>
 *   <Sidebar />
 *   <Box component="main" sx={{ flexGrow: 1 }}>
 *     <AppBar>
 *       <Toolbar>
 *         <MenuIcon onClick={toggleSidebar} />
 *         <Box sx={{ flexGrow: 1 }} />
 *         <Topbar />
 *       </Toolbar>
 *     </AppBar>
 *     <MainContent />
 *   </Box>
 * </Box>
 *
 * @example
 * // Integration with theme provider
 * <ThemeProvider theme={customTheme}>
 *   <CssBaseline />
 *   <Router>
 *     <Routes>
 *       <Route path="/admin/*" element={
 *         <AdminLayout>
 *           <Topbar />
 *           <AdminRoutes />
 *         </AdminLayout>
 *       } />
 *     </Routes>
 *   </Router>
 * </ThemeProvider>
 *
 * @see useSelector - Redux hook for accessing authentication state
 * @see useNavigate - React Router hook for navigation
 * @see useDispatch - Redux hook for dispatching actions
 * @see CircularProgress - Inline loading for profile and logout
 * @see MenuWrapper - Styled menu component
 * @see Api - API handler class for HTTP requests
 */
export default function Topbar() {
    const accessToken = useSelector((state) => state.Auth.accessToken);
    const profileImage = useSelector((state) => state.Auth);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [profileLoading, setProfileLoading] = useState(false);
    const [logoutLoading, setLogoutLoading] = useState(false);
    // eslint-disable-next-line no-unused-vars
    const [selectedImage, setSelectedImage] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const [openLogoutDialog, setOpenLogoutDialog] = useState(false);

    const handleMenu = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const LogoutHandler = () => {
        handleClose(); // Close menu
        setOpenLogoutDialog(true);
    };

    const handleLogoutConfirm = () => {
        setLogoutLoading(true);
        const logoutData = {
            accessToken
        };
        const data = [];
        data['data'] = logoutData;
        API.post(API_LOGOUT, data)
            .then((response) => {
                dispatch(logoutUser());
                setOpenLogoutDialog(false);
                setLogoutLoading(false);
                navigate(URL_DASHBOARD);
                toast.success(response.message);
            })
            .catch((error) => {
                setLogoutLoading(false);
                toast.error(error?.response?.data?.message || SERVER_ERROR);
            });
    };

    const handleLogoutCancel = () => {
        setOpenLogoutDialog(false);
    };

    const ProfileViewHandler = () => {
        handleClose(); // Close menu
        navigate(URL_PROFILE);
    };

    const getUserProfileDetails = useCallback(async () => {
        setProfileLoading(true);
        API.get(`${API_PROFILE}`)
            .then((response) => {
                const userProfile = response.data;
                setSelectedImage(response.data.profilePicUrl);
                const data = JSON.parse(sessionStorage.getItem('adminData'));
                data.firstName = userProfile?.firstName;
                data.lastName = userProfile?.lastName;
                data.email = userProfile?.email;
                data.profilePicUrl = userProfile?.profilePicUrl;
                dispatch(updateUser(response.data));
                setProfileLoading(false);
            })
            .catch((error) => {
                setProfileLoading(false);
                toast.error(error?.response?.data?.message || SERVER_ERROR);
            });
    }, [dispatch]);

    useEffect(() => {
        getUserProfileDetails();
    }, [getUserProfileDetails]);

    return (
        <Box>
            <div>
                <div
                    onClick={handleMenu}
                    style={{
                        border: '3px solid #f5e0b5',
                        borderRadius: '100%',
                        height: '45px',
                        width: '45px',
                        cursor: 'pointer',
                        padding: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative'
                    }}>
                    {profileLoading && (
                        <CircularProgress
                            size={28}
                            thickness={4}
                            sx={{
                                position: 'absolute',
                                color: '#e0eeff'
                            }}
                        />
                    )}
                    <img
                        alt="profile-user"
                        width="33px"
                        height="33px"
                        loading="lazy"
                        decoding="async"
                        src={
                            profileImage.adminData?.profilePicUrl === '' || !profileImage.adminData?.profilePicUrl
                                ? blankImage
                                : profileImage.adminData.profilePicUrl
                        }
                        style={{
                            borderRadius: '100%',
                            opacity: profileLoading ? 0.35 : 1
                        }}
                    />
                </div>
                <MenuWrapper
                    id="menu-appbar"
                    anchorEl={anchorEl}
                    anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'right'
                    }}
                    keepMounted
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right'
                    }}
                    open={Boolean(anchorEl)}
                    onClose={handleClose}>
                    <MenuItem onClick={ProfileViewHandler}>
                        <PersonOutlinedIcon sx={{ mr: 1 }} />
                        Profile
                    </MenuItem>
                    <MenuItem onClick={LogoutHandler}>
                        <LogoutOutlinedIcon sx={{ mr: 1 }} />
                        Logout
                    </MenuItem>
                </MenuWrapper>
            </div>

            {/* Logout Confirmation Dialog */}
            <Dialog
                open={openLogoutDialog}
                onClose={() => {
                    if (!logoutLoading) {
                        handleLogoutCancel();
                    }
                }}
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                        minWidth: '320px'
                    }
                }}>
                <DialogTitle
                    sx={{
                        pb: 1,
                        color: '#1a237e',
                        fontWeight: 600
                    }}>
                    Confirm Logout
                </DialogTitle>
                <DialogContent>
                    <Typography>Are you sure you want to logout from your account?</Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={handleLogoutCancel}
                        disabled={logoutLoading}
                        variant="outlined"
                        sx={{
                            textTransform: 'none',
                            borderRadius: 1.5,
                            px: 2,
                            py: 0.8,
                            fontSize: '0.95rem'
                        }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleLogoutConfirm}
                        disabled={logoutLoading}
                        variant="contained"
                        color="error"
                        sx={{
                            textTransform: 'none',
                            borderRadius: 1.5,
                            px: 2,
                            py: 0.8,
                            minWidth: 100,
                            fontSize: '0.95rem',
                            backgroundColor: '#d32f2f',
                            '&:hover': {
                                backgroundColor: '#b71c1c'
                            }
                        }}>
                        {logoutLoading ? (
                            <CircularProgress size={22} color="inherit" />
                        ) : (
                            'Logout'
                        )}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
